if (!customElements.get('embroidery-popup')) {
  class EmbroideryPopup extends HTMLElement {
    constructor() {
      super();
      this.contentLoaded = false;
      this.triggerElement = null;
      this.open = false;

      this.selectors = {
        trigger: '.embroidery-trigger',
        overlay: '.embroidery-overlay',
        bundleDataScript: 'script[type="application/json"]',
      };

      this.boundMethods = {
        toggle: this.toggle.bind(this),
      };

      this.isSmpBundle = this.hasAttribute('data-is-smp-bundle');
    }

    connectedCallback() {
      this.elements = {
        trigger: document.querySelector(this.selectors.trigger),
        overlay: document.querySelector(this.selectors.overlay),
        bundleDataScript: this.querySelector(this.selectors.bundleDataScript),
      };

      this.attachEventListeners();
    }

    disconnectedCallback() {
      this.clearEventListeners();
    }

    attachEventListeners() {
      this.elements.trigger.addEventListener('click', this.boundMethods.toggle);
      this.elements.overlay.addEventListener('click', this.boundMethods.toggle);
    }

    clearEventListeners() {
      this.elements.trigger.removeEventListener('click', this.boundMethods.toggle);
      this.elements.overlay.removeEventListener('click', this.boundMethods.toggle);
    }

    toggle() {
      if (!this.contentLoaded || this.isSmpBundle) {
        this.loadHTML();
      }

      this.elements.overlay.classList.toggle('active');
      this.classList.toggle('active');
      this.open = !this.open;

      if (this.open) {
        this.scrollPosition = window.pageYOffset;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.width = '100%';
      } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, this.scrollPosition);
      }
    }

    loadHTML() {
      fetch(`/products/${this.dataset.productHandle}?view=embroidery`)
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          if (this.isSmpBundle) {
            this.modifyBundleContent(doc);
          }

          this.innerHTML = doc.querySelector('.embroidery-main-content').outerHTML;
          this.contentLoaded = true;
        })
        .catch((error) => {
          console.error('Error loading embroidery HTML:', error);
        });
    }

    modifyBundleContent(doc) {
      const bundleDataScript = this.elements.bundleDataScript;

      if (bundleDataScript) {
        try {
          const bundleData = JSON.parse(bundleDataScript.textContent);

          const titleElements = doc.querySelectorAll('.embroidery-product-title');
          if (titleElements.length > 0 && bundleData.bundleTitle) {
            titleElements.forEach((element) => {
              element.textContent = bundleData.bundleTitle;
            });
          }

          const priceElement = doc.querySelector('.embroidery-product-price-original');
          if (priceElement && bundleData.bundlePrice) {
            if (bundleData.hasGlobalDiscount && bundleData.hasGlobalDiscount > 0) {
              priceElement.innerHTML = `
                <span class="embroidery-discounted-price">${bundleData.bundlePrice}</span>
                <del class="embroidery-original-price">${bundleData.originalBundlePrice}</del>
              `;
            } else {
              priceElement.textContent = bundleData.bundlePrice;
            }
          }

          const mobilePriceElement = doc.querySelector('.embroidery-product-price.mobile-only');
          if (mobilePriceElement && bundleData.bundlePrice) {
            const monogrammingFee = mobilePriceElement.textContent.match(/\(\+[^)]+\)/);
            if (bundleData.hasGlobalDiscount && bundleData.hasGlobalDiscount > 0) {
              mobilePriceElement.innerHTML = `
                <span class="embroidery-discounted-price">${bundleData.bundlePrice}</span>
                <del class="embroidery-original-price">${bundleData.originalBundlePrice}</del>
                ${monogrammingFee ? monogrammingFee[0] : ''}
              `;
            } else {
              mobilePriceElement.innerHTML = `${bundleData.bundlePrice} ${monogrammingFee ? monogrammingFee[0] : ''}`;
            }
          }

          doc.querySelector('.embroidery-atc').textContent = 'Add bundle to bag';
        } catch (error) {
          console.error('Error parsing bundle data:', error);
        }
      }
    }
  }

  customElements.define('embroidery-popup', EmbroideryPopup);
}

if (!customElements.get('embroidery-content')) {
  class EmbroideryContent extends HTMLElement {
    constructor() {
      super();

      this.embroideryProductId = this.dataset.embroideryProductId;

      this.selectors = {
        popup: 'embroidery-popup',
        closeButton: '.embroidery-close-button',
        textInput: '.embroidery-text-input',
        fontInput: '.font-input',
        letters: '.embroidery-letters',
        swatches: '.embroidery-swatch',
        activeColorName: '.active-color-name',
        atc: '.embroidery-atc',
        form: 'product-form form',
        textHiddenInput: 'input[name="properties[Text]"]',
        styleHiddenInput: 'input[name="properties[Style]"]',
        colorHiddenInput: 'input[name="properties[Color]"]',
        smpBundle: 'smp-bundle',
      };

      this.boundMethods = {
        handleTextInputChange: this.handleTextInputChange.bind(this),
        handleSwatchChange: this.handleSwatchChange.bind(this),
        handleFontChange: this.handleFontChange.bind(this),
        addToCart: this.addToCart.bind(this),
      };
    }

    connectedCallback() {
      this.elements = {
        popup: document.querySelector(this.selectors.popup),
        closeButton: this.querySelectorAll(this.selectors.closeButton),
        textInput: this.querySelector(this.selectors.textInput),
        fontInputs: this.querySelectorAll(this.selectors.fontInput),
        letters: this.querySelector(this.selectors.letters),
        swatches: this.querySelectorAll(this.selectors.swatches),
        activeColorName: this.querySelector(this.selectors.activeColorName),
        atc: this.querySelector(this.selectors.atc),
        mainProductForm: document.querySelector(this.selectors.form),
        textHiddenInput: document.querySelector(this.selectors.textHiddenInput),
        styleHiddenInput: document.querySelector(this.selectors.styleHiddenInput),
        colorHiddenInput: document.querySelector(this.selectors.colorHiddenInput),
      };

      if (this.elements.popup.dataset.isSmpBundle === 'true') {
        this.elements.smpBundle = document.querySelector(this.selectors.smpBundle);
      }

      this.activeFont = this.elements.fontInputs[0].dataset.displayName;

      this.boundMethods.toggle = this.elements.popup.toggle.bind(this.elements.popup);

      this.attachEventListeners();
    }

    disconnectedCallback() {
      this.clearEventListeners();
    }

    attachEventListeners() {
      this.elements.closeButton.forEach((closeButton) => {
        closeButton.addEventListener('click', this.boundMethods.toggle);
      });
      this.elements.textInput.addEventListener('input', this.boundMethods.handleTextInputChange);
      this.elements.swatches.forEach((swatch) => {
        swatch.addEventListener('change', this.boundMethods.handleSwatchChange);
      });
      this.elements.atc.addEventListener('click', this.boundMethods.addToCart);
      this.elements.fontInputs.forEach((fontInput) => {
        fontInput.addEventListener('change', this.boundMethods.handleFontChange);
      });
    }

    clearEventListeners() {
      this.elements.closeButton.forEach((closeButton) => {
        closeButton.removeEventListener('click', this.boundMethods.toggle);
      });
      this.elements.textInput.removeEventListener('input', this.boundMethods.handleTextInputChange);
      this.elements.swatches.forEach((swatch) => {
        swatch.removeEventListener('change', this.boundMethods.handleSwatchChange);
      });
      this.elements.atc.removeEventListener('click', this.boundMethods.addToCart);
      this.elements.fontInputs.forEach((fontInput) => {
        fontInput.removeEventListener('change', this.boundMethods.handleFontChange);
      });
    }

    handleTextInputChange(e) {
      const sanitizedValue = e.target.value.replace(/[^a-zA-Z]/g, '');

      if (sanitizedValue.length > 0) {
        this.elements.atc.disabled = false;
      } else {
        this.elements.atc.disabled = true;
      }

      this.elements.letters.textContent = sanitizedValue;
      e.target.value = sanitizedValue;
    }

    handleSwatchChange(e) {
      this.elements.letters.style.setProperty('--color', e.target.value);
      this.elements.activeColorName.textContent = e.target.dataset.colorName;
    }

    handleFontChange(e) {
      this.elements.letters.style.setProperty('--font', e.target.value);
      this.elements.letters.dataset.font = e.target.value;
      this.activeFont = e.target.dataset.displayName;
    }

    addToCart() {
      this.elements.popup.toggle();

      if (this.elements.popup.dataset.isSmpBundle === 'true') {
        this.elements.smpBundle.dispatchEvent(
          new CustomEvent('embroidery-atc', {
            detail: {
              embroideryProductId: this.embroideryProductId,
              text: this.elements.letters.textContent,
              style: this.activeFont,
              color: this.elements.activeColorName.textContent,
            },
          }),
        );
        return;
      }

      this.elements.textHiddenInput.value = this.elements.letters.textContent;
      this.elements.textHiddenInput.disabled = false;
      this.elements.styleHiddenInput.value = this.activeFont;
      this.elements.styleHiddenInput.disabled = false;
      this.elements.colorHiddenInput.value = this.elements.activeColorName.textContent;
      this.elements.colorHiddenInput.disabled = false;

      this.elements.mainProductForm.dispatchEvent(
        new CustomEvent('embroidery-atc', {
          detail: {
            embroideryProductId: this.embroideryProductId,
          },
        }),
      );
    }
  }

  customElements.define('embroidery-content', EmbroideryContent);
}
